import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { useAmbientBackground } from '../hooks/useAmbientBackground';
import { useNightModeStyles } from '../hooks/useNightModeStyles';
import { useOrbPosition } from '../hooks/useOrbPosition';
import { TemperatureUnitProvider } from '../contexts/TemperatureUnitContext';
import WeatherHeader from './WeatherHeader';
import InfoDialog from './InfoDialog';
import FlipCounter from './FlipCounter';
import PWAInstallButton from './PWAInstallButton';
import ModeToggle from './ModeToggle';
import { useGlobalCounter } from '../hooks/useGlobalCounter';
import { trackEvent } from '../lib/analytics';
import '../styles/hourly-gradients.css';
import '../styles/atmospheric-noise.css';

/**
 * Shared page shell: gradient + orb + noise background, theme toggle, counter,
 * mode toggle, header heading, content slot, footer (feedback + PWA), info dialog.
 *
 * Used by WeatherPage (/) and TripPage (/trip) so both surfaces stay visually
 * identical without copy-pasting chrome.
 */
const PageShell = ({ heading, children, onCount }) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { gradientClass } = useAmbientBackground();
  const { getNightModeClass } = useNightModeStyles();
  const orbPosition = useOrbPosition();
  const { count, increment, isLoading } = useGlobalCounter();

  // Expose the increment callback to children that need it (Weather + Trip).
  React.useEffect(() => {
    if (onCount) onCount(increment);
  }, [increment, onCount]);

  const handleFeedbackClick = () => {
    trackEvent('click', { content_type: 'button', item_id: 'Feedback Button' });
    if (window.Tally?.openPopup) {
      window.Tally.openPopup('wAbjZo', {
        layout: 'modal',
        width: 700,
        hideTitle: true,
        onSubmit: () => {
          trackEvent('feedback_submit', { content_type: 'feedback', item_id: 'Layer Recommendation' });
        }
      });
      return;
    }
    // Fallback: Tally script hasn't loaded yet (1s setTimeout in index.html),
    // or it's blocked by an adblocker / network policy. Open the form directly.
    window.open('https://tally.so/r/wAbjZo', '_blank', 'noopener,noreferrer');
  };

  return (
    <TemperatureUnitProvider>
      <div className={`min-h-screen w-full bg-gray-50 dark:bg-gray-900 ${gradientClass} ${getNightModeClass()} flex flex-col items-center justify-center p-4 sm:p-6 relative font-inter overflow-hidden`}>
        <div className="noise-overlay"></div>
        <div
          className="orb-effect"
          style={{
            top: `${orbPosition.top}%`,
            left: `${orbPosition.left}%`
          }}
        ></div>
        <WeatherHeader showBanner={false} />

        <Button
          variant="ghost"
          onClick={toggleTheme}
          className="absolute top-4 right-4 p-1 text-gray-800 dark:text-gray-200 button-transparent"
          aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>

        <FlipCounter count={count} isLoading={isLoading} />

        <ModeToggle />

        <h1 className="text-2xl sm:text-3xl font-light text-gray-800 dark:text-gray-200 mb-6 text-center px-2">
          {heading}
        </h1>

        <div className="w-full max-w-md">
          {children}
        </div>

        <div className="flex flex-row justify-center items-center gap-3 mt-6 mb-2 px-4 flex-wrap">
          <Button variant="outline" onClick={handleFeedbackClick} className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 rounded-full">
            <span role="img" aria-label="feedback">📮</span>
            <span>Feedback</span>
          </Button>
          <PWAInstallButton />
          <InfoDialog
            isOpen={isInfoOpen}
            onOpenChange={setIsInfoOpen}
            triggerClassName="rounded-full h-10 w-10 border border-foreground/20 bg-background/20 backdrop-blur-md hover:bg-foreground/10"
          />
        </div>
      </div>
    </TemperatureUnitProvider>
  );
};

export default PageShell;
